import Typography from '@mui/material/Typography'
import React from 'react'

import { canCategorize, canVoiceTranscribe } from '@/app/module/campaigns/ai-helpers'
import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { useCampaignActionContext } from '@/app/module/campaigns/component/item-provider'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import { CampaignTemplateType, LocalAction, LocalOpenEnded, PartFamily } from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'

import { ActionProps, ApiProps } from './part-config-props'
import ResponseOpenEndedValidation from './response-open-ended-validation'
import ResponseTextCategories from './response-text-categories'
import WhatsAppTranscribeToggle from './whatsapp-transcribe-toggle'

type Props = {
  actionProps: ActionProps
  apiProps: ApiProps
  campaignType: CampaignTemplateType
  id: string
  invalidReplyActions?: LocalAction[]
  openEnded: LocalOpenEnded
  partType: PartFamily
  transcribe: boolean

  onChange: (value: { openEnded?: LocalOpenEnded; transcribe?: boolean }) => void
  onValidationChange?: (openEnded: LocalOpenEnded) => void
}

// CATI - SMS - WhatsApp Question Open response
const ResponseTextOpenEnded: React.FC<Props> = ({
  actionProps,
  apiProps,
  campaignType,
  id,
  invalidReplyActions = [],
  openEnded,
  partType,
  transcribe,
  onChange,
  onValidationChange,
}) => {
  const { hasQuotas } = useCampaignActionContext()
  const openEndedActions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType,
        partType,
        hasQuotas,
        actionNamespace: ActionContext.OpenEnded,
      }),
    [campaignType, hasQuotas, partType],
  )
  const openEndedCategoryActions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType,
        partType,
        hasQuotas,
        actionNamespace: ActionContext.OpenEndedCategory,
      }),
    [campaignType, hasQuotas, partType],
  )

  const handleActionsChange = React.useCallback(
    (actionsList: LocalAction[]) => {
      onChange({
        openEnded: {
          ...openEnded,
          actions: actionsList || openEnded.actions || [],
        },
      })
    },
    [openEnded, onChange],
  )
  return (
    <div className="campaign-open-ended-tab-content">
      <Typography>Open Response</Typography>
      <div>
        <Typography color="textSecondary" variant="caption" gutterBottom={true}>
          Specify what we should do after the contact responds.
        </Typography>
        {canVoiceTranscribe(partType) && (
          <WhatsAppTranscribeToggle
            context={ActionContext.OnAnswer}
            transcribe={transcribe}
            onChange={(v) => onChange({ transcribe: v })}
          />
        )}
        {(partType === PartFamily.SMS || partType === PartFamily.WhatsApp) && (
          <ResponseOpenEndedValidation
            hasInvalidReplyActions={invalidReplyActions.length > 0}
            openEnded={openEnded}
            onChange={(value) => (onValidationChange ? onValidationChange(value) : onChange({ openEnded: value }))}
          />
        )}
        <ActionsBox
          actionOptions={openEndedActions}
          actions={openEnded.actions || []}
          defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.smsQuestion}
          hasTranscribe={canVoiceTranscribe(partType)}
          imageId={`${id}-${-1}`}
          isCloseDisabled={true}
          transcribeEnabled={transcribe}
          updateContactConfig={{
            invalid: false,
            openResponse: true,
            question: false,
          }}
          voiceId={`${id}-${-1}`}
          onChange={handleActionsChange}
          {...actionProps}
          {...apiProps}
        />
        {canCategorize(campaignType) && (
          <ResponseTextCategories
            actionOptions={openEndedCategoryActions}
            actionProps={actionProps}
            apiProps={apiProps}
            id={id}
            openEnded={openEnded}
            onChange={onChange}
          />
        )}
      </div>
    </div>
  )
}

export default React.memo(ResponseTextOpenEnded)
